import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SettlementCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const settlements = [
    { date: '2026-01-18', amount: 18450.32, rail: 'Bitcoin', status: 'processing' },
    { date: '2026-01-20', amount: 12450.00, rail: 'Ethereum', status: 'scheduled' },
    { date: '2026-01-22', amount: 8520.00, rail: 'Stablecoin', status: 'scheduled' },
    { date: '2026-01-25', amount: 15680.00, rail: 'Bitcoin', status: 'scheduled' },
    { date: '2026-01-27', amount: 9240.50, rail: 'Merchant', status: 'scheduled' },
    { date: '2026-01-30', amount: 22340.15, rail: 'Ethereum', status: 'scheduled' }
  ];

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const getSettlementForDate = (date) => {
    const dateStr = date?.toISOString()?.split('T')?.[0];
    return settlements?.filter(s => s?.date === dateStr);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'processing': return 'bg-warning';
      case 'scheduled': return 'bg-primary';
      default: return 'bg-muted';
    }
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const days = [];
  
  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    days?.push(null);
  }
  
  // Add days of month
  for (let day = 1; day <= daysInMonth; day++) {
    days?.push(day);
  }

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth() + 1));
  };

  const handleDateClick = (day) => {
    if (!day) return;
    const date = new Date(currentMonth?.getFullYear(), currentMonth?.getMonth(), day);
    const daySettlements = getSettlementForDate(date);
    if (daySettlements?.length > 0) {
      setSelectedDate({ date, settlements: daySettlements });
    }
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Settlement Calendar</h3>
          <p className="text-sm text-muted-foreground">Visualize upcoming payout schedules</p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
          <Icon name="Calendar" size={20} className="text-accent" />
        </div>
      </div>

      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" iconName="ChevronLeft" onClick={handlePrevMonth} />
        <h4 className="text-base font-semibold text-foreground">
          {currentMonth?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h4>
        <Button variant="ghost" size="sm" iconName="ChevronRight" onClick={handleNextMonth} />
      </div>

      {/* Calendar Grid */}
      <div className="mb-4">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
            <div key={day} className="text-center text-xs font-semibold text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {days?.map((day, index) => {
            if (!day) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const date = new Date(currentMonth?.getFullYear(), currentMonth?.getMonth(), day);
            const daySettlements = getSettlementForDate(date);
            const hasSettlement = daySettlements?.length > 0;
            const isToday = new Date()?.toDateString() === date?.toDateString();

            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                className={`aspect-square p-1 rounded-lg text-sm font-medium transition-smooth ${
                  isToday ? 'bg-primary/20 text-primary' : 'text-foreground hover:bg-muted/50'
                } ${hasSettlement ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <span>{day}</span>
                  {hasSettlement && (
                    <div className="flex gap-0.5 mt-1">
                      {daySettlements?.map((settlement, idx) => (
                        <div
                          key={idx}
                          className={`w-1.5 h-1.5 rounded-full ${getStatusColor(settlement?.status)}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground">
              {selectedDate?.date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </h4>
            <Button variant="ghost" size="sm" iconName="X" onClick={() => setSelectedDate(null)} />
          </div>
          <div className="space-y-2">
            {selectedDate?.settlements?.map((settlement, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-muted/20 rounded-lg">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    ${settlement?.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-muted-foreground">{settlement?.rail}</p>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(settlement?.status)} text-white`}>
                  {settlement?.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="pt-4 border-t border-border">
        <p className="text-xs font-semibold text-muted-foreground mb-2">Status Legend</p>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-success" />
            <span className="text-xs text-muted-foreground">Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-warning" />
            <span className="text-xs text-muted-foreground">Processing</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-xs text-muted-foreground">Scheduled</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettlementCalendar;